/**
 * @file PlanTypeGeneralTab.js
 * @description General tab inside the Display Properties modal.
 */

import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import PopulationGroupMultiSelect from '../../shared/asyncSearchableMultiSelect/PopulationGroupMultiSelect';
import Tooltip from '../../ui/tooltips/Tooltip';
import styles from '../PlanTypeDisplayProperties.module.scss';
import radioButtonStyles from '../PlanYear.module.scss';
import {
    DISPLAY_MODE,
    DISPLAY_MODE_OPTIONS,
    resolveDisplayMode,
} from '../_helpers/planTypeDisplayMode';
import { getText } from '../../../i18n';

const NAME_LIMIT = 50;
const CODE_LIMIT = 50;

const PlanTypeGeneralTab = ({ viewModel, draft, onChange, isAddMode }) => {
    const isSystemPlanType = viewModel?.planType?.tenantId == null;
    const isNameCodeReadOnly = !isAddMode && isSystemPlanType;

    const seededName = draft?.nameOverride ?? viewModel?.planType?.name ?? '';
    const seededCode =
        draft?.codeOverride ??
        viewModel?.code ??
        viewModel?.planType?.code ??
        '';
    const seededDisplayMode =
        draft?.displayMode ??
        (isAddMode
            ? DISPLAY_MODE.ENROLLMENT_ONLY
            : resolveDisplayMode(viewModel));
    const seededGroups = draft?.populationGroups ?? [];

    // local controlled-input mirror, kept in sync with the draft state held by the modal
    const [name, setName] = useState(seededName);
    const [code, setCode] = useState(seededCode);
    const [displayMode, setDisplayMode] = useState(seededDisplayMode);
    const [selectedGroups, setSelectedGroups] = useState(seededGroups);

    useEffect(() => {
        setName(seededName);
        setCode(seededCode);
        setDisplayMode(seededDisplayMode);
        setSelectedGroups(seededGroups);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewModel?.id, draft?.displayMode, draft?.populationGroups?.length]);

    const pushUp = patch => {
        onChange({
            nameOverride: name,
            codeOverride: code,
            displayMode,
            populationGroups: selectedGroups,
            ...patch,
        });
    };

    const handleName = e => {
        const next = e.target.value.slice(0, NAME_LIMIT);
        setName(next);
        pushUp({ nameOverride: next });
    };

    const handleCode = e => {
        const next = e.target.value.slice(0, CODE_LIMIT);
        setCode(next);
        pushUp({ codeOverride: next });
    };

    const handleDisplayMode = next => {
        setDisplayMode(next);
        // clearing groups when switching away from Population keeps the
        // payload tidy and matches the docx's intent.
        const groups = next === DISPLAY_MODE.POPULATION ? selectedGroups : [];
        setSelectedGroups(groups);
        pushUp({ displayMode: next, populationGroups: groups });
    };

    const handleGroupsChange = groups => {
        setSelectedGroups(groups);
        pushUp({ populationGroups: groups });
    };

    const populationDisabled = displayMode !== DISPLAY_MODE.POPULATION;

    return (
        <div>
            <div className={styles.nameCodeRow}>
                <div className={styles.nameCodeField}>
                    <label className={styles.fieldLabel} htmlFor="planTypeName">
                        {getText('msg_709a23220f2c')}<span className={styles.required}>*</span>
                        {isNameCodeReadOnly && (
                            <Tooltip
                                html="System plan types cannot change Name or Code."
                                placement="right"
                            >
                                <i
                                    className={`bi bi-info-circle ${styles.readOnlyInfoIcon}`}
                                />
                            </Tooltip>
                        )}
                    </label>
                    <input
                        id="planTypeName"
                        type="text"
                        className={`form-control ${
                            isNameCodeReadOnly ? styles.readOnlyInput : ''
                        }`}
                        placeholder={isAddMode ? 'Plan Name' : ''}
                        value={name}
                        onChange={handleName}
                        maxLength={NAME_LIMIT}
                        readOnly={isNameCodeReadOnly}
                    />
                    <div className={styles.fieldHelp}>
                        {getText('msg_971d6ded4edf')} {name.length}/{NAME_LIMIT}
                    </div>
                </div>

                <div className={styles.nameCodeField}>
                    <label className={styles.fieldLabel} htmlFor="planTypeCode">
                        {getText('msg_adac69379a62')}<span className={styles.required}>*</span>
                        {isNameCodeReadOnly && (
                            <Tooltip
                                html="System plan types cannot change Name or Code."
                                placement="right"
                            >
                                <i
                                    className={`bi bi-info-circle ${styles.readOnlyInfoIcon}`}
                                />
                            </Tooltip>
                        )}
                    </label>
                    <input
                        id="planTypeCode"
                        type="text"
                        className={`form-control ${
                            isNameCodeReadOnly ? styles.readOnlyInput : ''
                        }`}
                        placeholder={isAddMode ? 'Plan Code' : ''}
                        value={code}
                        onChange={handleCode}
                        maxLength={CODE_LIMIT}
                        readOnly={isNameCodeReadOnly}
                    />
                    <div className={styles.fieldHelp}>
                        {getText('msg_971d6ded4edf')} {code.length}/{CODE_LIMIT}
                    </div>
                </div>
            </div>

            <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>
                    {getText('msg_3370400f77d8')}<span className={styles.required}>*</span>
                </label>
                <div className={styles.radioRow}>
                    {DISPLAY_MODE_OPTIONS.map(opt => (
                        <label
                            key={opt.value}
                            htmlFor={`dm-${opt.value}`}
                            className={radioButtonStyles.radioButtonWrapper}
                        >
                            <input
                                id={`dm-${opt.value}`}
                                type="radio"
                                name="planTypeDisplayMode"
                                value={opt.value}
                                checked={displayMode === opt.value}
                                onChange={() => handleDisplayMode(opt.value)}
                            />
                            <span className={radioButtonStyles.customRadio} />
                            {opt.label}
                        </label>
                    ))}
                </div>
            </div>

            <div
                className={`${styles.fieldGroup} ${
                    populationDisabled
                        ? styles.populationGroupDisabled
                        : styles.populationGroupEnabled
                }`}
            >
                <PopulationGroupMultiSelect
                    key={populationDisabled ? 'disabled' : 'active'}
                    selectedGroups={selectedGroups}
                    onGroupsChange={handleGroupsChange}
                    label={getText('msg_e744dd6b3c48')}
                    isRequired={!populationDisabled}
                    showSelectedItemsInField={true}
                    showSelectionBox={true}
                    skipFetch={populationDisabled}
                />
            </div>
        </div>
    );
};

PlanTypeGeneralTab.propTypes = {
    viewModel: PropTypes.object,
    draft: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    isAddMode: PropTypes.bool,
};

export default PlanTypeGeneralTab;
